import rawEntries from './entries.json';

/**
 * "✨ New in Board Game Stats" feed cards.
 *
 * Every user-facing feature PR adds one entry to entries.json, so its card goes
 * live with the frontend deploy that ships the feature. whatsNew.test.ts checks
 * every entry (fields, link to a real route, image size).
 */
export interface WhatsNewEntry {
  /** Stable kebab-case id, also the localStorage dismiss key. */
  id: string;
  /** When the feature was merged (ISO 8601 with time zone), used to sort it among feed events. */
  date: string;
  title: string;
  /** One line, ≤140 chars. */
  blurb: string;
  /** In-app path for "Try it →", e.g. "/tools/finger-picker". */
  link: string;
  /** Optional screenshot under public/whats-new/ (small WebP/PNG, ≤60 KB). */
  image?: string;
  /** PR number that shipped the feature. */
  pr?: number;
}

export const WHATS_NEW_ENTRIES: WhatsNewEntry[] = rawEntries as WhatsNewEntry[];

/** Cards older than this drop out of the feed. */
export const WHATS_NEW_MAX_AGE_DAYS = 30;

const DISMISS_KEY = 'whatsNewDismissed';

export function getDismissedIds(): Set<string> {
  try {
    const stored = JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]');
    return new Set(Array.isArray(stored) ? stored.filter((x) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function dismissEntry(id: string): void {
  const ids = getDismissedIds();
  ids.add(id);
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // Storage full or blocked: the card just comes back next visit
  }
}

/**
 * Entries to show in the feed: not dismissed, not older than 30 days, and not
 * in the future. When more feed pages exist, entries older than the oldest
 * loaded event wait until that page loads, so they land in date order.
 */
export function visibleWhatsNewEntries(
  entries: WhatsNewEntry[],
  options: { now?: Date; dismissed?: Set<string>; oldestLoaded?: string | null; hasMore?: boolean } = {},
): WhatsNewEntry[] {
  const now = (options.now ?? new Date()).getTime();
  const dismissed = options.dismissed ?? new Set<string>();
  const cutoff = now - WHATS_NEW_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  const floor = options.hasMore && options.oldestLoaded ? Date.parse(options.oldestLoaded) : -Infinity;
  return entries.filter((e) => {
    const t = Date.parse(e.date);
    return !dismissed.has(e.id) && t >= cutoff && t <= now && t >= floor;
  });
}

const ID_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/** Problems with one entry ([] when valid). Route and image checks live in the test. */
export function validateEntry(entry: Partial<WhatsNewEntry>): string[] {
  const errors: string[] = [];
  if (!entry.id || !ID_RE.test(entry.id)) errors.push('id must be kebab-case');
  if (!entry.date || !ISO_RE.test(entry.date) || Number.isNaN(Date.parse(entry.date))) {
    errors.push('date must be ISO 8601 with a time zone, e.g. 2026-11-05T00:31:35Z');
  }
  if (!entry.title || !entry.title.trim()) errors.push('title is required');
  else if (entry.title.length > 60) errors.push('title must be ≤60 chars');
  if (!entry.blurb || !entry.blurb.trim()) errors.push('blurb is required');
  else if (entry.blurb.length > 140) errors.push('blurb must be ≤140 chars');
  if (!entry.link || !entry.link.startsWith('/') || entry.link.startsWith('//')) {
    errors.push('link must be an in-app path starting with /');
  }
  if (entry.image !== undefined && !/^\/whats-new\/[a-z0-9-]+\.(webp|png|jpg)$/.test(entry.image)) {
    errors.push('image must be /whats-new/<name>.webp|png|jpg');
  }
  if (entry.pr !== undefined && (!Number.isInteger(entry.pr) || entry.pr <= 0)) errors.push('pr must be a PR number');
  return errors;
}
